import { Box, Flex, Grid, Section, Text, Button } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { apiRequest } from "@/lib/lutely-api/api-request";
import { isAxiosError } from "axios";
import { z } from "zod";
import { useState } from "react";
import { toast } from "react-toastify";
import { cn } from "@/lib/utils";
import {
  LABELS_PATH,
  MARKETING_PRODUCTS,
  PACKAGE_PRODUCTS,
  PREPARE_PRODUCTS,
  type MarketingProductKey,
} from "@/components/marketing/products";

const CURRENT_YEAR = new Date().getFullYear();

const productLinks = (keys: MarketingProductKey[]) =>
  keys.map((key) => ({
    label: MARKETING_PRODUCTS[key].name,
    to: MARKETING_PRODUCTS[key].path,
  }));

const FOOTER_COLUMNS = [
  { title: "Tools", links: productLinks(PREPARE_PRODUCTS) },
  {
    title: "Marketplace",
    links: [
      ...productLinks(PACKAGE_PRODUCTS),
      { label: "For Labels", to: LABELS_PATH },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "Pricing", to: "/upgrade" },
      { label: "Marketplace", to: "/marketplace" },
      { label: "About Us", to: "/about-us" },
      { label: "Terms of Use", to: "/terms" },
      { label: "Privacy Policy", to: "/privacy" },
      { label: "DMCA Policy", to: "/dmca" },
      { label: "Acceptable Use Policy", to: "/acceptable-use" },
    ],
  },
  {
    title: "Follow us",
    links: [
      {
        label: "Youtube",
        to: "https://www.youtube.com/@joinlutely",
        external: true,
      },
      {
        label: "Instagram",
        to: "https://www.instagram.com/joinlutely/",
        external: true,
      },
      {
        label: "TikTok",
        to: "https://www.tiktok.com/@joinlutely",
        external: true,
      },
      {
        label: "Facebook",
        to: "https://www.facebook.com/joinlutely/",
        external: true,
      },
      {
        label: "X",
        to: "https://x.com/joinlutely",
        external: true,
      },
    ],
  },
];

const newsletterEmailSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email." }),
});

function FooterLinkItem({
  link,
}: {
  link: { label: string; to: string; external?: boolean };
}) {
  return (
    <Link
      to={link.to}
      target={link.external ? "_blank" : undefined}
      rel={link.external ? "noopener noreferrer" : undefined}
      className="text-blue-900 text-[14px] hover:text-brand-primary"
    >
      {link.label}
    </Link>
  );
}

type FooterProps = {
  /** Fills the strip above the curved top edge, for pages that end on a dark section. */
  topClassName?: string;
};

export function Footer({ topClassName }: FooterProps = {}) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const hasError = error !== null;

  const handleSubscribe = async () => {
    const result = newsletterEmailSchema.safeParse({ email });

    if (!result.success) {
      setError(result.error.issues[0].message);
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      await apiRequest({
        url: "/newsletter/subscribe",
        method: "POST",
        data: { email: result.data.email, source: "footer_modal" },
      });

      toast.success("You're subscribed!", {
        autoClose: 5000,
      });
      setEmail("");
    } catch (e) {
      setError(
        isAxiosError(e) && e.response?.status === 409
          ? "This email is already subscribed."
          : "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Flex direction="column" gap="0" align="center">
      <Box width="100%" className="text-blue-900">
        <div className={topClassName}>
          <svg
            className="w-full relative top-px"
            viewBox="0 0 1440 18"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 11.032C0 11.032 398.809 0 720 0C1041.19 0 1440 11.032 1440 11.032V18H0V11.032Z"
              fill="currentColor"
            />
          </svg>
        </div>
        <Section
          height={{ initial: "auto", sm: "180px" }}
          mb="8"
          className="bg-blue-900 flex items-center"
        >
          <Flex
            direction={{ initial: "column", sm: "row" }}
            align="center"
            justify="between"
            width={{ initial: "100%", lg: "80%" }}
            maxWidth="1700px"
            mx="auto"
            px={{ initial: "6", lg: "0" }}
          >
            <Flex direction="column" align="center" gap="1">
              <Box
                mb={{ initial: "6", sm: "0" }}
                mr={{ initial: "2", md: "0" }}
                px={{ initial: "2", sm: "0" }}
              >
                <Text className="font-figtree text-[24px] sm:text-[36px] text-teal-500 font-semibold block">
                  Stay in the loop.
                </Text>
                <Text className="text-[14px] sm:text-[16px] text-white font-semibold block">
                  Sign up and we’ll let you know when important updates drop.
                </Text>
              </Box>
            </Flex>
            {/* Email Subscription */}
            <Flex
              direction="column"
              align={{ initial: "stretch", md: "center" }}
              gap="1"
              pb={{ initial: "6", sm: "0" }}
            >
              <Flex
                width={{ initial: "100%", md: "460px" }}
                px="2"
                py="2"
                className={cn(
                  "rounded-full bg-[rgba(255,255,255,0.20)]",
                  hasError && "border border-red-500",
                )}
              >
                <form
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubscribe();
                  }}
                  className="flex w-full"
                >
                  <input
                    type="email"
                    placeholder="Enter Your Email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (hasError) setError(null);
                    }}
                    className="flex-1 !bg-transparent px-4 text-[14px] !text-white focus:outline-none"
                  />

                  <Button
                    type="submit"
                    radius="full"
                    size="2"
                    className="text-white text-[14px] font-figtree font-semibold px-5 ml-2 bg-brand-primary hover:bg-blue-700"
                    disabled={submitting}
                  >
                    Subscribe
                  </Button>
                </form>
              </Flex>
              {hasError && (
                <Text
                  role="alert"
                  className="text-[12px] text-red-300 pl-3 lg:pl-0"
                >
                  {error}
                </Text>
              )}
              <Text className="text-[12px] text-white pl-3 lg:pl-0">
                By signing up, you agree to receive emails from Lutely.
                Unsubscribe anytime.
              </Text>
            </Flex>
          </Flex>
        </Section>
      </Box>

      {/* Signup/login and footer links */}
      <Flex
        direction={{ initial: "column", sm: "row" }}
        justify="between"
        gap={{ initial: "8", sm: "0" }}
        px={{ initial: "6", lg: "0" }}
        width={{ initial: "100%", lg: "80%" }}
        maxWidth="1700px"
        pb="96px"
      >
        <Flex direction="column" justify="between" align="start">
          <Link
            to="."
            search={{ auth: "signup" as const }}
            resetScroll={false}
            className="inline-flex h-12 items-center rounded-full bg-teal-500 px-10 font-figtree text-[17px] font-semibold text-blue-900 transition-colors hover:bg-teal-600"
          >
            Start free
          </Link>
          <Text className="text-[12px] text-gray-8 hidden md:block">
            © {CURRENT_YEAR} License Lounge, Inc. d/b/a Lutely
          </Text>
        </Flex>
        <Grid columns={{ initial: "2", md: "4" }} gap="8">
          {FOOTER_COLUMNS.map((column) => (
            <Box width="auto" key={column.title}>
              <Flex direction="column" gap="3">
                <Text className="text-brand-primary text-[14px] mb-2 font-semibold">
                  {column.title}
                </Text>

                {column.links.map((link) => (
                  <FooterLinkItem key={link.label} link={link} />
                ))}
              </Flex>
            </Box>
          ))}
        </Grid>
      </Flex>
      <Text className="text-[12px] text-gray-8 mt-9 pl-5 block md:hidden self-start">
        © {CURRENT_YEAR} License Lounge, Inc. d/b/a Lutely
      </Text>
    </Flex>
  );
}

export default Footer;
